import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import UploadButton from './UploadButton';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import Toast from 'react-bootstrap/Toast';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { API_BASE } from '../utils/apiBase';

export default function ImageManager({ folder, title }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [sorting, setSorting] = useState(false);
  const [draggedImageId, setDraggedImageId] = useState(null);
  const [dragOverImageId, setDragOverImageId] = useState(null);
  const { user } = useAuth();

  const fetchImages = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(
        `${API_BASE}/list-images?folder=${encodeURIComponent(folder)}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`
          }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al cargar imagenes');
      }

      setImages(data.images || []);
      setStatusMessage('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [folder, user]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const handleDelete = async () => {
    if (!imageToDelete) return;

    setDeleteLoading(true);
    try {
      const response = await fetch(`${API_BASE}/delete-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`
        },
        body: JSON.stringify({ publicId: imageToDelete.id })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al eliminar imagen');
      }

      setImages((currentImages) =>
        currentImages.filter((img) => img.id !== imageToDelete.id)
      );
      setShowDeleteModal(false);
      setImageToDelete(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const openDeleteModal = (image) => {
    setImageToDelete(image);
    setShowDeleteModal(true);
  };

  const moveImage = (currentImages, sourceId, targetId) => {
    const sourceIndex = currentImages.findIndex((image) => image.id === sourceId);
    const targetIndex = currentImages.findIndex((image) => image.id === targetId);

    if (sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) {
      return currentImages;
    }

    const nextImages = [...currentImages];
    const [movedImage] = nextImages.splice(sourceIndex, 1);
    nextImages.splice(targetIndex, 0, movedImage);
    return nextImages;
  };

  const persistImageOrder = async (nextImages, previousImages) => {
    setSorting(true);
    setError('');
    setStatusMessage('Guardando orden...');

    try {
      const response = await fetch(`${API_BASE}/reorder-images`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`
        },
        body: JSON.stringify({
          folder,
          orderedIds: nextImages.map((image) => image.id)
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'No se pudo guardar el orden');
      }

      setImages(nextImages.map((image, index) => ({ ...image, sort_order: index })));
      setStatusMessage('Orden actualizado');
    } catch (err) {
      setImages(previousImages);
      setError(err.message);
      setStatusMessage('');
    } finally {
      setSorting(false);
      window.setTimeout(() => {
        setStatusMessage('');
      }, 1600);
    }
  };

  const handleDragStart = (imageId) => {
    setDraggedImageId(imageId);
    setDragOverImageId(null);
    setStatusMessage('');
  };

  const handleDragOver = (event, targetImageId) => {
    event.preventDefault();

    if (!draggedImageId || sorting || dragOverImageId === targetImageId) {
      return;
    }

    setDragOverImageId(targetImageId);
  };

  const handleDragEnd = () => {
    setDraggedImageId(null);
    setDragOverImageId(null);
  };

  const handleDrop = (targetImageId) => {
    if (!draggedImageId || draggedImageId === targetImageId || sorting) {
      setDraggedImageId(null);
      setDragOverImageId(null);
      return;
    }

    const previousImages = images;
    const nextImages = moveImage(previousImages, draggedImageId, targetImageId);

    setDraggedImageId(null);
    setDragOverImageId(null);

    if (nextImages === previousImages) {
      return;
    }

    setImages(nextImages);
    persistImageOrder(nextImages, previousImages);
  };

  const draggedIndex = images.findIndex((image) => image.id === draggedImageId);
  const dragOverIndex = images.findIndex((image) => image.id === dragOverImageId);
  const dragDirection =
    draggedIndex !== -1 && dragOverIndex !== -1
      ? dragOverIndex > draggedIndex
        ? 'after'
        : 'before'
      : null;

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-2">Cargando imagenes...</p>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>{title}</h4>
        <UploadButton folder={folder} onUploadSuccess={fetchImages} />
      </div>

      {images.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <p>No hay imagenes en esta categoria.</p>
          <p>Usa "Subir imagenes" para agregar nuevas fotos.</p>
        </div>
      ) : (
        <Row className="g-3">
          {images.map((image) => (
            <Col key={image.id} xs={6} md={4} lg={3}>
              <div
                className={`position-relative admin-image-card${draggedImageId === image.id ? ' is-dragging' : ''}${dragOverImageId === image.id && draggedImageId !== image.id ? ' is-drop-target' : ''}${dragOverImageId === image.id && draggedImageId !== image.id && dragDirection === 'before' ? ' is-drop-before' : ''}${dragOverImageId === image.id && draggedImageId !== image.id && dragDirection === 'after' ? ' is-drop-after' : ''}`}
                draggable={!sorting}
                onDragStart={() => handleDragStart(image.id)}
                onDragEnd={handleDragEnd}
                onDragOver={(event) => handleDragOver(event, image.id)}
                onDrop={() => handleDrop(image.id)}
              >
                <img
                  src={image.thumbnail}
                  alt={image.id}
                  className="img-fluid rounded"
                  style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                />
                <div className="admin-image-meta">
                  <span>Arrastra para reordenar</span>
                </div>
                <div className="position-absolute top-0 end-0 p-2">
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => openDeleteModal(image)}
                  >
                    <i className="bi bi-trash" aria-hidden="true"></i>
                  </Button>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      )}

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar eliminacion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {imageToDelete ? (
            <>
              <p>Esta accion elimina la imagen de Cloudinary y del sitio.</p>
              <img
                src={imageToDelete.thumbnail}
                alt="Vista previa"
                className="img-fluid rounded"
                style={{ maxHeight: '200px' }}
              />
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={deleteLoading}
          >
          {deleteLoading ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="admin-toast-stack">
        {error ? (
          <Toast
            show
            onClose={() => setError('')}
            bg="danger"
            className="admin-toast"
            delay={4000}
            autohide
          >
            <Toast.Header closeButton>
              <strong className="me-auto">Error</strong>
            </Toast.Header>
            <Toast.Body>{error}</Toast.Body>
          </Toast>
        ) : null}

        {statusMessage ? (
          <Toast
            show
            onClose={() => setStatusMessage('')}
            bg={sorting ? 'secondary' : 'success'}
            className="admin-toast"
            delay={1800}
            autohide={!sorting}
          >
            <Toast.Header closeButton={!sorting}>
              <strong className="me-auto">{sorting ? 'Guardando' : 'Listo'}</strong>
            </Toast.Header>
            <Toast.Body>{statusMessage}</Toast.Body>
          </Toast>
        ) : null}
      </div>
    </>
  );
}
